import React, { Dispatch } from 'react';
import { Back, BGSide, Content, ContentWrapper, Wrapper } from './styled';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { LoginForm } from './sections/Login';
import { WelcomeLogin } from './sections/Welcome';
import { RootAction, State } from 'store/state';
import { investorsLoginStepSelector } from 'store/state/app/selectors';
import { connect } from 'react-redux';
import { AuthenticationModalType } from 'components/authentication/types';
import { AuthCode } from './sections/AuthCode';
import { SetPassword } from './sections/SetPassword';
import { InvestorsLoginTerms } from './sections/InvestorsLoginTerms';
import { EnterPassword } from './sections/EnterPassword';
import { Terms } from './sections/Terms';
import { TimeoutStep } from 'components/authentication/timeout';
import { setInvestorsLoginStep } from 'store/state/app/actions';
import { isConnectedUserSelector, userHasAcceptedInvestorsTermsSelector, userLoadingSelector } from 'store/state/domainData/selectors';
import BackIcon from 'assets/svg/back.svg';


interface LoginPageProps {
  step: AuthenticationModalType;
  onSetActiveModal: (type: AuthenticationModalType) => void;
  isUserLoading?: boolean;
  hideBack?: boolean;
  isConnected?: boolean;
}

const LoginPageBase: React.FC<LoginPageProps> = ({ step, onSetActiveModal, isUserLoading, hideBack, isConnected }) => {
  const constructImageURI = useImageUrlBuilder();
  const screen = useScreenBreakpoint();
  const isDevice = screen < 3;

  if (isUserLoading) return null;
  // a connected user never needs the Welcome sign-in step (step === null): after accepting the
  // terms it would otherwise flash during the modal's exit animation
  if (isConnected && step === null) return null;

  let content = null;

  const handleBack = () => {
    onSetActiveModal(null);
  };

  switch (true) {
    case step === AuthenticationModalType.SignIn || step === AuthenticationModalType.SignUp:
      content = <ContentWrapper><LoginForm /></ContentWrapper>;
      break;
    case step === AuthenticationModalType.UserEmailVerification:
      content = <ContentWrapper><AuthCode /></ContentWrapper>;
      break;
    case step === AuthenticationModalType.SetPassword:
      content = <ContentWrapper><SetPassword /></ContentWrapper>;
      break;
    case step === AuthenticationModalType.InvestorsLoginTerms:
      content = <ContentWrapper><InvestorsLoginTerms /></ContentWrapper>;
      break;
    case step === AuthenticationModalType.EnterPassword:
      content = <ContentWrapper><EnterPassword /></ContentWrapper>;
      break;
    case step === AuthenticationModalType.Consents:
      content = <ContentWrapper><Terms /></ContentWrapper>;
      break;
    case step === AuthenticationModalType.VerificationTimeout:
      content = <ContentWrapper><TimeoutStep setActiveModal={onSetActiveModal} /></ContentWrapper>;
      break;
    case step === null:
      content = <WelcomeLogin />;
      break;
  }

  return (
    <Wrapper notFirstMobileStep={step !== null && isDevice}>
      <Content>
        {content}
        {step && !hideBack ? (
          <Back onClick={handleBack}>
            <BackIcon width={40} height={40} />
          </Back>
        ) : null}
      </Content>
      {step === null || !isDevice ? <BGSide bg={constructImageURI(`investors/phones_${isDevice ? 'mobile' : 'desktop_2'}.jpg`)} /> : null}
    </Wrapper>
  );
};

const mapStateToProps = (state: State) => {
  const rawStep = investorsLoginStepSelector(state);
  const isConnected = isConnectedUserSelector(state);
  // a connected user who hasn't accepted the investors T&C has exactly one step — the terms
  // confirm. Deriving it here (instead of seeding the step on every modal open, which the
  // open-reducer would reseed anyway) covers every entry point and removes the Welcome hop.
  const step = rawStep === null && isConnected && !userHasAcceptedInvestorsTermsSelector(state)
    ? AuthenticationModalType.InvestorsLoginTerms
    : rawStep;
  return {
    step,
    isUserLoading: userLoadingSelector(state),
    // Back would drop an already-signed-in user onto the Welcome sign-in step — a dead end
    hideBack: step === AuthenticationModalType.InvestorsLoginTerms && isConnected,
    isConnected,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onSetActiveModal: (type: AuthenticationModalType) => dispatch(setInvestorsLoginStep({ type }, { isUserInitiated: true })),
});

export const LoginPage = connect(mapStateToProps, mapDispatchToProps)(LoginPageBase);
